import store, { getSlice } from 'modules/shared/stores/store';
import {
    addModal,
    addPortal,
    getLastAddedPortalId,
    removeModal,
    removePortal,
} from 'modules/shared/stores/portals-slice-handler';
import { addToaster, addSnackbar, addConfirmationDialog } from 'utils/dialogs-manager';
import {
    openFloatingWindow,
    closeFloatingWindow,
    getLastAddedFloatingWindowId,
    toggleFloatingWindowVisibility,
} from 'modules/shared/stores/floating-windows-slice-handler';
import { openSlidingLayer, closeSlidingLayer } from 'modules/shared/stores/sliding-layers-slice-handler';
import { userSettingsSliceHandler } from 'modules/settings/stores/user-settings-slice-handler';
import { topBarSliceHandler } from 'modules/top-bar/stores/top-bar-slice-handler';
import { applyOutputDeviceChangesInDom } from 'modules/sound-settings/stores/sound-settings-slice-handler';
import { masterPagesSliceHandler } from 'master-pages/stores/master-pages-slice-handler';
import {
    openContactsSelectorSlider,
    closeContactsSelectorSlider,
    openContactsSelectorWithClientInSlidingLayer,
    openUserGroupDialog,
} from 'utils/contact-util';
import { myRulesPackagesSliceHandler } from 'modules/top-bar/stores/my-rules-packages-slice-handler';
import {
    registerContactsSelector,
    unregisterContactsSelector,
    getUserDetails,
    getUser,
    getGroupDetails,
    getGroup,
    openContactDetails,
    openPbxDialer,
    openUserDetails,
} from '../modules/contacts/stores/contacts-slice-handler';
import LayoutManager from './layout-manager';
import LocalEvent from './local-event';
import Log from './log';
import sdkClient from './sdk-client';
import t from './translate';
import {
    deleteRequestsByChannel,
    setConnectedChannelsCount,
} from '../modules/reach-back-requests/stores/reach-back-requests-slice-handler';
import ConfigIniUtil from './config-ini-util';
import { removeStreamer } from '../modules/video-popups/stores/video-streaming-slice-handler';

/**
 * This class is a utility that allows the external world (such as WebChat) to interact with UI app.
 */
class WorldInterface {
    /**
     * Singleton instance.
     * @return {WorldInterface}
     */
    static getInstance() {
        if (typeof WorldInterface.instance !== 'undefined') {
            return WorldInterface.instance;
        }

        WorldInterface.instance = new WorldInterface();

        return WorldInterface.instance;
    }

    /**
     * This function allows external world to relate with the local events triggered inside the UI app.
     * @param {string} eventType
     * @param {function} callback
     * @param {boolean} volatile If the listener should be discarded after its execution.
     */
    setEventListener(eventType, callback, volatile = false) {
        try {
            LocalEvent.setListener(eventType, callback, volatile);
        } catch (e) {
            // fallback silently without breaking the client
            Log.error('WORLD INTERFACE', e.message);
        }
    }

    /**
     * This function allows setting a SINGLE listener of the given callback.
     * @param {string} eventType
     * @param {function} callback
     */
    setUniqueEventListener(eventType, callback) {
        try {
            LocalEvent.setListener(eventType, callback, false, true);
        } catch (e) {
            // fallback silently without breaking the client
            Log.error('WORLD INTERFACE', e.message);
        }
    }

    getLayoutManager() {
        return LayoutManager;
    }

    getValueFromCssVar(cssVarName) {
        return LayoutManager.getValueFromCssVar(cssVarName);
    }

    /**
     * Print the state of the app from store.
     * @return object
     */
    debugState() {
        return store.getState();
    }

    debugSliceHandler = (handlerName) => getSlice(handlerName);

    debugConfigIni() {
        ConfigIniUtil.debug();
    }

    /**
     * LAZY Render a Rect component inside the given element.
     * @param {PortalOptions} options
     * @return Promise<string> The id of the added portal.
     */
    renderComponent(options) {
        return new Promise((resolve) => {
            store.dispatch(addPortal(options));

            setTimeout(() => {
                resolve(getLastAddedPortalId());
            });
        });
    }

    /**
     * Remove from DOM a component which has been rendered with renderComponent.
     * @param componentUuid
     */
    removeComponent(componentUuid) {
        store.dispatch(removePortal(componentUuid));
    }

    /**
     * Creates a floating window which renderers a component.
     * @param {FloatingWindowOptions} options
     * @return Promise<string> The id of the opened floating window.
     */
    openFloatingWindow(options) {
        return new Promise((resolve) => {
            openFloatingWindow(options);

            setTimeout(() => {
                resolve(getLastAddedFloatingWindowId());
            });
        });
    }

    /**
     * Close a floating window.
     * @param {string} floatingWindowId The id of a floating window which has been opened with openFloatingWindow.
     */
    closeFloatingWindow(floatingWindowId) {
        closeFloatingWindow(floatingWindowId);
    }

    /**
     * Open a modal which has a component inside.
     * @param {WcfUiModalOptions} options
     * @return {string} modalId The id of the registered modal. Useful for closind the modal from outside.
     */
    openModal(options) {
        return addModal(options);
    }

    closeModal(modalId) {
        removeModal(modalId);
    }

    /**
     * Hide/Show a floating window.
     * @param {string} floatingWindowId The id of a floating window which has been opened with openFloatingWindow.
     */
    toggleFloatingWindowVisibility(floatingWindowId) {
        toggleFloatingWindowVisibility(floatingWindowId);
    }

    /**
     * Creates a sliding layer which renderers a component.
     * @param {WcfUiSlidingLayerOptions} options
     * @return {string} The id of the registered sliding layer.
     */
    openSlidingLayer = (options) => openSlidingLayer(options);

    /**
     * Close a sliding layer.
     * @param {string} slidingLayerId The id of a registered sliding layer which has been opened with openSlidingLayer.
     */
    closeSlidingLayer = (slidingLayerId) => closeSlidingLayer(slidingLayerId);

    /**
     * Open a Slider which has a ContactsSelector inside
     * @param {ContactsSelectorOptions} contactsSelectorOptions
     * @param {WcfUiSlidingLayerOptions|null} rawSlidingLayerOptions
     * @return {string} The id of the registered slider.
     */
    openContactsSelectorSlider = (contactsSelectorOptions, rawSlidingLayerOptions = null) => {
        const contactsSelectorUuid = registerContactsSelector();

        if (!rawSlidingLayerOptions) {
            rawSlidingLayerOptions = {};
        }

        const slidingLayerOptions = {
            ...rawSlidingLayerOptions,
            cssClassName: 'wcf-ui-contacts-selector-layer',
            onClose: () => {
                // when closing must unregister the contacts selector
                unregisterContactsSelector(contactsSelectorUuid);

                if (typeof rawSlidingLayerOptions.onClose === 'function') {
                    rawSlidingLayerOptions.onClose();
                }
            },
        };

        return openContactsSelectorSlider(contactsSelectorUuid, contactsSelectorOptions, slidingLayerOptions);
    };

    /**
     * Close a contacts selector slider.
     * @param {string} slidingLayerId An ide obtained with openContactsSelectorSlider
     */
    closeContactsSelectorSlider = (slidingLayerId) => closeContactsSelectorSlider(slidingLayerId);

    /**
     * Open a sliding layer which renders a ContactsSelector, and a main area layer which renders a ContactsSelectorClient linked with the selector.
     * @param {WcfUiSlidingLayerOptions} slidingLayerOptions
     * @param {ContactsSelectorOptions} selectorOptions
     * @param {ContactsSelectorClientOptions} clientOptions
     */
    openContactsSelectorWithClient = (slidingLayerOptions, selectorOptions, clientOptions) =>
        openContactsSelectorWithClientInSlidingLayer(slidingLayerOptions, selectorOptions, clientOptions);

    /**
     * Open the window with UI from the WebChat.
     * This is supposed to be called only when the WCFUI is embedded under webchat page.
     */
    openFromWebChat() {
        let url = '';

        Object.keys(wcfUiCfg).forEach((cfgKey) => {
            // on its own the interface needs empty opener property
            if (cfgKey === 'opener') {
                return;
            }

            const val = typeof wcfUiCfg[cfgKey] === 'object' ? JSON.stringify(wcfUiCfg[cfgKey]) : wcfUiCfg[cfgKey];

            url += `&${cfgKey}=${val}`;
        });

        // remove the first &
        url = `${WebchatURI}/ui/?${url.substring(1)}`;

        window.open(url, '_blank');
    }

    /**
     * Set a listener for the events received over the web socket created by the SDK.
     * @param {string} eventType
     * @param {Function} callback
     */
    setSocketEventListener(eventType, callback) {
        sdkClient.setSocketEventListener(eventType, callback);
    }

    /**
     * Remove a listener which has been set for a socket event.
     * @param {string} eventType
     * @param {Function} callback
     */
    removeSocketEventListener(eventType, callback) {
        sdkClient.setSocketEventListener(eventType, callback);
    }

    /**
     * Emit an event over the web-socket created by the SDK.
     * @param {string} eventType
     * @param {Object} params The payload to be added in the event.
     * @param {boolean} disableLog If the SDK should write that in its log por not.
     */
    emitSocketEvent(eventType, params = {}, disableLog = false) {
        sdkClient.emitSocketEvent(eventType, params, disableLog);
    }

    /**
     * Set a listener for the INTERNAL events manipulated by the SDK.
     * @param {string} eventType
     * @param {Function} callback
     */
    setSdkEventManagerListener(eventType, callback) {
        sdkClient.setEventManagerListener(eventType, callback);
    }

    /**
     * Translate a label suing the locales from WCFUI.
     * @param {string} label
     * @return string
     */
    translate = (label) => t(label);

    /**
     * Show a snackbar element.
     * @param {SnackbarOptions} options
     */
    addSnackbar = (options) => addSnackbar(options);

    /**
     * Show a toaster element.
     * @param {ToasterOptions} options
     */
    addToaster = (options) => addToaster(options);

    /**
     * Show a standalone confirmation dialog.
     * @param {ConfirmationDialogOptions} options
     */
    addConfirmationDialog = (options) => addConfirmationDialog(options);

    /**
     * @return {UserSettingsJson|*}
     */
    getUserSettings = () => userSettingsSliceHandler.getUserSettingsJson();

    /**
     * Select a top-menu item
     * @param {MainMenuItemType} item
     */
    setSelectedMenuItem = (item) => topBarSliceHandler.setSelectedMenuItem(item);

    /**
     * Select a top-menu item
     * @param {MainMenuItemType} item
     */
    getSelectedMenuItem = () => topBarSliceHandler.getSelectedMenuItem();

    /**
     * Sets a status for emergency calls
     * @param {EmergencyStatus} status
     */
    setEmergencyCallStatus = (status) => topBarSliceHandler.setEmergencyCallStatus(status);

    /**
     * Sets a status for emergency messages
     * @param {EmergencyStatus} status
     */
    setEmergencyMessageStatus = (status) => topBarSliceHandler.setEmergencyMessageStatus(status);

    /**
     * Sets a circle with a counter over specific menu items
     * @param {MainMenuItemType} menuType
     * @param {number} circleValue
     */
    setMenuItemCircleValue = (menuType, circleValue) =>
        topBarSliceHandler.setMenuItemCircleValue(menuType, circleValue);

    /** When closing the legacy bell notifications popup, this changes the button state in the top bar */
    setBellNotificationsOpen = () => topBarSliceHandler.setBellNotificationsOpen();

    /**
     * Updates a property from the user's JSON settings.
     * @param settingName
     * @param settingValue
     */
    updateUserSetting = (settingName, settingValue) =>
        userSettingsSliceHandler.updateJsonSetting(settingName, settingValue);

    /**
     * Set the master page which should take over the entire screen.
     * @param {MainMenuItemType} activePage
     */
    setActiveMasterPage = (activePage) => masterPagesSliceHandler.setActivePage(activePage);

    /**
     * Set height of the call/channel banners space which must be taken in account when rendering the master pages.
     * @param {number} bannersSpaceHeight
     */
    setBannersSpaceHeight = (bannersSpaceHeight) => masterPagesSliceHandler.setBannersSpaceHeight(bannersSpaceHeight);

    getUserDetails = (msisdn) => getUserDetails(msisdn);

    getGroupDetails = (groupId) => getGroupDetails(groupId);

    deleteRequestsByChannel = (channelId) => deleteRequestsByChannel(channelId);

    setConnectedChannelsCount = (count) => setConnectedChannelsCount(count);

    /**
     * Propagate the selected output device over the audio elements found in DOM.
     */
    applyOutputDeviceChangesInDom = () => applyOutputDeviceChangesInDom();

    getSelectedMyRulesPackageUuid = () => myRulesPackagesSliceHandler.getSelectedPackageUuid();

    myRulesPackageExists = (id) => myRulesPackagesSliceHandler.packageExists(id);

    getMyRulesPackage = (id) => myRulesPackagesSliceHandler.getPackage(id);

    getUser = (msisdn) => getUser(msisdn);

    getGroup = (groupId) => getGroup(groupId);

    /**
     * Open the details of a contact.
     * @param {string|number} contactId  The Msisdn of a user or GroupId of a group.
     */
    openContactDetails = (contactId) => openContactDetails(contactId, false);

    /**
     * Open the details of a user.
     * @param {string} msisdn  The Msisdn of a user or GroupId of a group.
     */
    openUserDetails = (msisdn) => openUserDetails(msisdn);

    openUserGroupDialog = (editedGroupId) => openUserGroupDialog([], editedGroupId);

    /**
     * Open the PBX dialer.
     * @param {(phoneNumber: string, internalContact: CugContact|null) => void} callback
     */
    openPbxDialer = (callback = null) => openPbxDialer(callback);

    removeStreamingPopup = (msisdn) => removeStreamer(msisdn);
}
export default WorldInterface.getInstance();
